/**
 * CostConfigDialog - Dialog para configuracao de custos
 *
 * Permite configurar:
 * - Combustivel (consumo, preco, tipo)
 * - Motorista (valor hora, hora extra)
 * - Manutencao (custo por km)
 * - Pedagios (estimativa ou manual)
 * - Outros custos
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  LocalGasStation as FuelIcon,
  Person as DriverIcon,
  Build as MaintenanceIcon,
  Toll as TollIcon,
  MoreHoriz as OtherIcon,
  ExpandMore as ExpandIcon,
  Save as SaveIcon,
  Restore as ResetIcon,
  Close as CloseIcon,
  DirectionsCar as CarIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import {
  DEFAULT_COST_CONFIG,
  FUEL_TYPES,
  VEHICLE_PROFILES,
  saveCostConfig,
  loadCostConfig,
  resetCostConfig,
  formatCurrency,
} from '../services/costCalculationService';

const CostConfigDialog = ({
  open,
  onClose,
  onSave,
  initialConfig = null,
}) => {
  const tokens = usePremiumTokens();

  // Estado da configuracao
  const [config, setConfig] = useState(initialConfig || loadCostConfig());
  const [expandedSection, setExpandedSection] = useState('fuel');
  const [hasChanges, setHasChanges] = useState(false);

  // Carregar config ao abrir
  useEffect(() => {
    if (open) {
      const loaded = initialConfig || loadCostConfig();
      setConfig(loaded);
      setHasChanges(false);
    }
  }, [open, initialConfig]);

  // Atualizar campo
  const updateField = useCallback((section, field, value) => {
    setConfig((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
    setHasChanges(true);
  }, []);

  // Aplicar perfil de veiculo
  const applyVehicleProfile = useCallback((profileKey) => {
    const profile = VEHICLE_PROFILES[profileKey];
    if (!profile) return;

    setConfig((prev) => ({
      ...prev,
      fuel: {
        ...prev.fuel,
        consumptionKmL: profile.consumptionKmL,
        fuelType: profile.fuelType,
        pricePerLiter: FUEL_TYPES[profile.fuelType]?.avgPrice || prev.fuel.pricePerLiter,
      },
      maintenance: {
        ...prev.maintenance,
        costPerKm: profile.maintenancePerKm,
      },
    }));
    setHasChanges(true);
  }, []);

  // Salvar configuracao
  const handleSave = useCallback(() => {
    saveCostConfig(config);
    setHasChanges(false);
    if (onSave) {
      onSave(config);
    }
    onClose();
  }, [config, onSave, onClose]);

  // Resetar para padrao
  const handleReset = useCallback(() => {
    resetCostConfig();
    setConfig({ ...DEFAULT_COST_CONFIG });
    setHasChanges(true);
  }, []);

  // Mudar secao expandida
  const handleAccordionChange = (section) => (_, isExpanded) => {
    setExpandedSection(isExpanded ? section : null);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: tokens?.surface?.base,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${tokens?.border?.subtle}`,
          pb: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon sx={{ color: tokens?.accent?.primary }} />
          <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
            Configuracao de Custos
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Perfis de Veiculo */}
        <Box sx={{ p: 2, backgroundColor: tokens?.surface?.elevated }}>
          <Typography sx={{ color: tokens?.text?.secondary, fontSize: '12px', mb: 1 }}>
            APLICAR PERFIL DE VEICULO
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {Object.entries(VEHICLE_PROFILES).map(([key, profile]) => (
              <Chip
                key={key}
                label={profile.label}
                icon={<CarIcon />}
                onClick={() => applyVehicleProfile(key)}
                sx={{
                  backgroundColor: tokens?.surface?.raised,
                  color: tokens?.text?.primary,
                  '&:hover': {
                    backgroundColor: tokens?.accent?.muted,
                  },
                }}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          {/* Combustivel */}
          <Accordion
            expanded={expandedSection === 'fuel'}
            onChange={handleAccordionChange('fuel')}
            sx={{
              backgroundColor: tokens?.surface?.raised,
              '&:before': { display: 'none' },
              mb: 1,
            }}
          >
            <AccordionSummary expandIcon={<ExpandIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FuelIcon sx={{ color: tokens?.semantic?.warning }} />
                <Typography sx={{ fontWeight: 500 }}>Combustivel</Typography>
                <Chip
                  size="small"
                  label={formatCurrency(config.fuel.pricePerLiter) + '/L'}
                  sx={{ ml: 1, fontSize: '11px' }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de Combustivel</InputLabel>
                  <Select
                    value={config.fuel.fuelType}
                    onChange={(e) => {
                      updateField('fuel', 'fuelType', e.target.value);
                      // Atualizar preco medio
                      const avgPrice = FUEL_TYPES[e.target.value]?.avgPrice;
                      if (avgPrice) {
                        updateField('fuel', 'pricePerLiter', avgPrice);
                      }
                    }}
                    label="Tipo de Combustivel"
                  >
                    {Object.entries(FUEL_TYPES).map(([key, fuel]) => (
                      <MenuItem key={key} value={key}>
                        {fuel.label} (media {formatCurrency(fuel.avgPrice)}/L)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Consumo do Veiculo"
                  type="number"
                  size="small"
                  value={config.fuel.consumptionKmL}
                  onChange={(e) => updateField('fuel', 'consumptionKmL', parseFloat(e.target.value) || 0)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">km/L</InputAdornment>,
                  }}
                  helperText="Quantos km o veiculo faz por litro"
                />

                <TextField
                  label="Preco do Combustivel"
                  type="number"
                  size="small"
                  value={config.fuel.pricePerLiter}
                  onChange={(e) => updateField('fuel', 'pricePerLiter', parseFloat(e.target.value) || 0)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    endAdornment: <InputAdornment position="end">/litro</InputAdornment>,
                  }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Motorista */}
          <Accordion
            expanded={expandedSection === 'driver'}
            onChange={handleAccordionChange('driver')}
            sx={{
              backgroundColor: tokens?.surface?.raised,
              '&:before': { display: 'none' },
              mb: 1,
            }}
          >
            <AccordionSummary expandIcon={<ExpandIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DriverIcon sx={{ color: tokens?.accent?.primary }} />
                <Typography sx={{ fontWeight: 500 }}>Motorista</Typography>
                <Chip
                  size="small"
                  label={formatCurrency(config.driver.hourlyRate) + '/h'}
                  sx={{ ml: 1, fontSize: '11px' }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Valor por Hora"
                  type="number"
                  size="small"
                  value={config.driver.hourlyRate}
                  onChange={(e) => updateField('driver', 'hourlyRate', parseFloat(e.target.value) || 0)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    endAdornment: <InputAdornment position="end">/hora</InputAdornment>,
                  }}
                />

                <TextField
                  label="Horas Normais por Dia"
                  type="number"
                  size="small"
                  value={config.driver.dailyLimit}
                  onChange={(e) => updateField('driver', 'dailyLimit', parseInt(e.target.value) || 8)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">horas</InputAdornment>,
                  }}
                  helperText="Apos esse limite, aplica multiplicador de hora extra"
                />

                <TextField
                  label="Multiplicador Hora Extra"
                  type="number"
                  size="small"
                  value={config.driver.overtimeMultiplier}
                  onChange={(e) => updateField('driver', 'overtimeMultiplier', parseFloat(e.target.value) || 1.5)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">x</InputAdornment>,
                  }}
                  helperText="Ex: 1.5 = 50% a mais que hora normal"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Manutencao */}
          <Accordion
            expanded={expandedSection === 'maintenance'}
            onChange={handleAccordionChange('maintenance')}
            sx={{
              backgroundColor: tokens?.surface?.raised,
              '&:before': { display: 'none' },
              mb: 1,
            }}
          >
            <AccordionSummary expandIcon={<ExpandIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MaintenanceIcon sx={{ color: tokens?.text?.tertiary }} />
                <Typography sx={{ fontWeight: 500 }}>Manutencao</Typography>
                {config.maintenance.enabled && (
                  <Chip
                    size="small"
                    label={formatCurrency(config.maintenance.costPerKm) + '/km'}
                    sx={{ ml: 1, fontSize: '11px' }}
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.maintenance.enabled}
                      onChange={(e) => updateField('maintenance', 'enabled', e.target.checked)}
                    />
                  }
                  label="Incluir custo de manutencao"
                />

                {config.maintenance.enabled && (
                  <TextField
                    label="Custo por Km"
                    type="number"
                    size="small"
                    value={config.maintenance.costPerKm}
                    onChange={(e) => updateField('maintenance', 'costPerKm', parseFloat(e.target.value) || 0)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                      endAdornment: <InputAdornment position="end">/km</InputAdornment>,
                    }}
                    helperText="Inclui depreciacao, pneus, revisoes, etc."
                  />
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Pedagios */}
          <Accordion
            expanded={expandedSection === 'tolls'}
            onChange={handleAccordionChange('tolls')}
            sx={{
              backgroundColor: tokens?.surface?.raised,
              '&:before': { display: 'none' },
              mb: 1,
            }}
          >
            <AccordionSummary expandIcon={<ExpandIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TollIcon sx={{ color: tokens?.semantic?.info }} />
                <Typography sx={{ fontWeight: 500 }}>Pedagios</Typography>
                {config.tolls.enabled && (
                  <Chip
                    size="small"
                    label={`~${formatCurrency(config.tolls.estimatePerKm)}/km`}
                    sx={{ ml: 1, fontSize: '11px' }}
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.tolls.enabled}
                      onChange={(e) => updateField('tolls', 'enabled', e.target.checked)}
                    />
                  }
                  label="Incluir estimativa de pedagios"
                />

                {config.tolls.enabled && (
                  <>
                    <TextField
                      label="Estimativa por Km"
                      type="number"
                      size="small"
                      value={config.tolls.estimatePerKm}
                      onChange={(e) => updateField('tolls', 'estimatePerKm', parseFloat(e.target.value) || 0)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                        endAdornment: <InputAdornment position="end">/km</InputAdornment>,
                      }}
                      helperText="Valor medio de pedagios por km rodado"
                    />

                    <Alert severity="info" sx={{ fontSize: '12px' }}>
                      Para valores exatos, cadastre pedagios manualmente na rota.
                    </Alert>
                  </>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Outros Custos */}
          <Accordion
            expanded={expandedSection === 'other'}
            onChange={handleAccordionChange('other')}
            sx={{
              backgroundColor: tokens?.surface?.raised,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <OtherIcon sx={{ color: tokens?.text?.tertiary }} />
                <Typography sx={{ fontWeight: 500 }}>Outros Custos</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Seguro por Dia"
                  type="number"
                  size="small"
                  value={config.other.insurancePerDay}
                  onChange={(e) => updateField('other', 'insurancePerDay', parseFloat(e.target.value) || 0)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    endAdornment: <InputAdornment position="end">/dia</InputAdornment>,
                  }}
                />

                <TextField
                  label="Estacionamento (por rota)"
                  type="number"
                  size="small"
                  value={config.other.parkingEstimate}
                  onChange={(e) => updateField('other', 'parkingEstimate', parseFloat(e.target.value) || 0)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                  helperText="Estimativa de custo com estacionamento por rota"
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: `1px solid ${tokens?.border?.subtle}`,
          gap: 1,
        }}
      >
        <Button
          onClick={handleReset}
          startIcon={<ResetIcon />}
          sx={{ color: tokens?.text?.tertiary }}
        >
          Restaurar Padrao
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={!hasChanges}
          sx={{
            backgroundColor: tokens?.accent?.primary,
            '&:hover': { backgroundColor: tokens?.accent?.hover },
          }}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CostConfigDialog;
